import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Users, TrendingUp, DollarSign, Copy, Check, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';
import StatsCounter from '../components/StatsCounter';
import { API_URL as API } from '../config/api';

const AffiliateDashboard = () => {
  const { t, i18n } = useTranslation();
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Page-specific translations
  const pageTexts = {
    fr: {
      commissionRate: "Taux de Commission",
      referralLink: "Votre lien de parrainage :",
      shareOnSocial: "Partager sur les réseaux sociaux",
      promoBanners: "Bannières promotionnelles",
      promoBannersDesc: "Téléchargez des bannières pour promouvoir le marathon sur vos sites web, blogs ou réseaux sociaux",
      linkCopied: "Lien copié !",
      codeCopied: "Code copié !",
      bannerDownloaded: "Bannière téléchargée !",
      downloadError: "Erreur lors du téléchargement",
      shareMsg: "🎟️ Participez au Marathon de l'Avenir EDENLIFE 2026 et gagnez jusqu'à 450 000 USD ! Utilisez mon code:"
    },
    en: {
      commissionRate: "Commission Rate",
      referralLink: "Your referral link:",
      shareOnSocial: "Share on social media",
      promoBanners: "Promotional Banners",
      promoBannersDesc: "Download banners to promote the marathon on your websites, blogs or social media",
      linkCopied: "Link copied!",
      codeCopied: "Code copied!",
      bannerDownloaded: "Banner downloaded!",
      downloadError: "Download error",
      shareMsg: "🎟️ Join the EDENLIFE Marathon 2026 and win up to $450,000 USD! Use my code:"
    },
    ht: {
      commissionRate: "To Komisyon",
      referralLink: "Lyen parenaj ou:",
      shareOnSocial: "Pataje sou rezo sosyal",
      promoBanners: "Banyè Pwomosyonèl",
      promoBannersDesc: "Telechaje banyè pou pwomouvwa maraton an sou sit entènèt ou, blog oswa rezo sosyal",
      linkCopied: "Lyen kopye!",
      codeCopied: "Kòd kopye!",
      bannerDownloaded: "Banyè telechaje!",
      downloadError: "Erè telechajman",
      shareMsg: "🎟️ Patisipe nan Maraton EDENLIFE 2026 epi genyen jiska 450 000 USD! Sèvi ak kòd mwen:"
    },
    es: {
      commissionRate: "Tasa de Comisión",
      referralLink: "Tu enlace de referido:",
      shareOnSocial: "Compartir en redes sociales",
      promoBanners: "Banners Promocionales",
      promoBannersDesc: "Descarga banners para promocionar el maratón en tus sitios web, blogs o redes sociales",
      linkCopied: "¡Enlace copiado!",
      codeCopied: "¡Código copiado!",
      bannerDownloaded: "¡Banner descargado!",
      downloadError: "Error de descarga",
      shareMsg: "🎟️ ¡Participa en el Maratón EDENLIFE 2026 y gana hasta $450,000 USD! Usa mi código:"
    },
    pt: {
      commissionRate: "Taxa de Comissão",
      referralLink: "Seu link de indicação:",
      shareOnSocial: "Compartilhar nas redes sociais",
      promoBanners: "Banners Promocionais",
      promoBannersDesc: "Baixe banners para promover a maratona em seus sites, blogs ou redes sociais",
      linkCopied: "Link copiado!",
      codeCopied: "Código copiado!",
      bannerDownloaded: "Banner baixado!",
      downloadError: "Erro no download",
      shareMsg: "🎟️ Participe da Maratona EDENLIFE 2026 e ganhe até $450.000 USD! Use meu código:"
    }
  };

  const pt = pageTexts[i18n.language] || pageTexts.fr;

  useEffect(() => {
    const fetchAffiliate = async () => {
      try {
        const response = await axios.get(`${API}/affiliates/stats`, {
          withCredentials: true,
        });
        setAffiliate(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          setAffiliate(null);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAffiliate();
  }, []);

  const handleRegister = async () => {
    try {
      const response = await axios.post(
        `${API}/affiliates/register`,
        {},
        { withCredentials: true }
      );
      setAffiliate(response.data);
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(t('common.error'));
    }
  };

  const affiliateLink = affiliate ? `${window.location.origin}/purchase?ref=${affiliate.referral_code}` : '';
  const shortMessage = affiliate ? `${pt.shareMsg} ${affiliate.referral_code}\n${affiliateLink}` : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(affiliateLink);
    setCopiedLink(true);
    toast.success(pt.linkCopied);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(affiliate.referral_code);
    setCopiedCode(true);
    toast.success(pt.codeCopied);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(affiliateLink)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(`${pt.shareMsg} ${affiliate.referral_code}`);
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(affiliateLink)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(shortMessage);
    const url = `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  const handleDownloadBanner = async (size) => {
    try {
      const response = await axios.get(`${API}/affiliates/banner/${size}?code=${affiliate.referral_code}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `edenlife-banner-${size}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(pt.bannerDownloaded);
    } catch (error) {
      console.error('Failed to download banner:', error);
      toast.error(pt.downloadError);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="affiliate-loading">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div
        className="min-h-screen pt-24 px-6 flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}
        data-testid="affiliate-register-section"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-12 max-w-2xl text-center"
        >
          <Users className="w-20 h-20 text-primary mx-auto mb-6" />
          <h1
            className="text-4xl font-bold text-primary mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('affiliate.title')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">{t('affiliate.description')}</p>
          <Button
            onClick={handleRegister}
            size="lg"
            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-12 py-6 text-xl"
            data-testid="register-affiliate-button"
          >
            {t('affiliate.register')}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pt-24 px-6 pb-12"
      style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}
      data-testid="affiliate-dashboard-page"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1
            className="text-4xl md:text-5xl font-bold text-primary mb-12 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('affiliate.stats')}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <StatsCounter
              value={affiliate.total_sales}
              label={t('affiliate.totalSales')}
              prefix="$"
              color="text-primary"
            />
            <StatsCounter
              value={affiliate.commission_earned}
              label={t('affiliate.commission')}
              prefix="$"
              color="text-accent"
            />
            <StatsCounter
              value={Math.round((affiliate.commission_earned / affiliate.total_sales) * 100) || 10}
              label={pt.commissionRate}
              suffix="%"
              color="text-secondary"
            />
          </div>

          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 mb-8">
            <h2 className="text-2xl font-bold text-primary mb-6">{t('affiliate.yourCode')}</h2>
            <div className="flex gap-4 items-center mb-6">
              <div className="flex-1 bg-primary/10 rounded-xl p-6">
                <p className="text-4xl font-bold text-primary text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {affiliate.referral_code}
                </p>
              </div>
              <Button
                onClick={handleCopyCode}
                size="lg"
                variant="outline"
                className="rounded-full h-14 w-14 p-0"
                data-testid="copy-code-button"
              >
                {copiedCode ? <Check className="w-5 h-5 text-accent" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>

            <div className="bg-secondary/10 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-2">{pt.referralLink}</p>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={affiliateLink}
                  readOnly
                  className="flex-1 bg-white rounded-lg px-4 py-3 text-sm border border-gray-300"
                />
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  className="rounded-full"
                  data-testid="copy-link-button"
                >
                  {copiedLink ? <Check className="w-4 h-4 text-accent" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center">
                <Share2 className="w-6 h-6 mr-2" />
                {pt.shareOnSocial}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={handleShareFacebook}
                  className="bg-[#1877F2] text-white hover:bg-[#1877F2]/90 rounded-full"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </Button>
                <Button
                  onClick={handleShareTwitter}
                  className="bg-[#1DA1F2] text-white hover:bg-[#1DA1F2]/90 rounded-full"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                  Twitter
                </Button>
                <Button
                  onClick={handleShareWhatsApp}
                  className="bg-[#25D366] text-white hover:bg-[#25D366]/90 rounded-full"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
              <Download className="w-6 h-6 mr-2" />
              {pt.promoBanners}
            </h3>
            <p className="text-gray-600 mb-6">{pt.promoBannersDesc}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                onClick={() => handleDownloadBanner('728x90')}
                variant="outline"
                className="h-auto py-4 flex-col rounded-xl"
              >
                <div className="font-bold mb-1">Leaderboard</div>
                <div className="text-sm text-gray-600">728 × 90 px</div>
              </Button>
              <Button
                onClick={() => handleDownloadBanner('300x250')}
                variant="outline"
                className="h-auto py-4 flex-col rounded-xl"
              >
                <div className="font-bold mb-1">Medium Rectangle</div>
                <div className="text-sm text-gray-600">300 × 250 px</div>
              </Button>
              <Button
                onClick={() => handleDownloadBanner('160x600')}
                variant="outline"
                className="h-auto py-4 flex-col rounded-xl"
              >
                <div className="font-bold mb-1">Wide Skyscraper</div>
                <div className="text-sm text-gray-600">160 × 600 px</div>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;